import { ApiStatus } from "src/config/enums/genericEnums";
import { calCulateAdjustedExtendedRetail } from "src/pages/CreateFreightClaim/slice/createFcReducers";
import {
  formatCurrencyOrNumber,
  getItem,
  roundNumber
} from "src/utils/genericFunctions";
import { getFormattedDate } from "src/utils/getDates";
import stringToNumber from "src/utils/stringToNumber";
import { v4 as uuidv4 } from "uuid";
import {
  freightClaimPoItemMapper,
  mapDamagedQuantityWithReceived
} from "src/utils/freightClaimUtils";
import { matchPalletItem } from "src/utils/transferUtils";
import { IApiStatus, IGetStoreCreditDetails } from "../interface";
import {
  calculateEaches,
  isValidQuantity,
  mapPoItemsWithStoreCreditDetails
} from "src/utils/storeCreditUtils";

export const initialState: any = {
  poAdditionalDetails: {},
  poItems: null,
  poItemsCopy: [],
  searchQuery: "",
  searchResults: [],
  blockNavigation: true,
  wareHouse: "",
  poBasicData: {
    poType: "Freight Claim",
    supplier: "",
    effectiveDate: getFormattedDate(new Date(), "MM/DD/YYYY"),
    createdBy: "",
    purchasingOrganization: {
      name: "",
      responseCode: ""
    },
    invoiceNumber: ""
  },
  reasonForClaim: "",
  notes: "",
  attachments: [],
  createPOLoading: false,
  getPOItemsStatus: "pending",
  uploadAttachmentLoading: false,
  searchProductsLoading: false,
  margin: 0,
  totalCost: 0,
  status: "",
  approvedOrRejectedDate: "",
  refTransfer: "",
  transferItems: [],
  totalAdjustedRetail: 0,
  totalAdjustedRetailForMargin: 0,
  totTransferCost: 0,
  approverNotes: "",
  approverAttachments: [],
  referenceTransferReceivedDate: "",
  deletedAtttachmentPaths: [],
  overShortDocId: "",
  getStoreCreditDetailsStatus: "pending",
  getStoreCreditDetails: []
};

export const handleEditDamagedQuantity = (
  state: any,
  action: {
    payload: { uniqueId: string; quantity: { [key: string]: string } };
  }
) => {
  const { uniqueId, quantity } = action.payload;
  const value: any = getItem(state.poItemsCopy, uniqueId);

  /* logic for blocking user to enter quantity limit to total received */
  if (
    isValidQuantity(
      quantity.damagedQuantityCases,
      quantity.damagedQuantityEaches,
      value,
      "FC"
    )
  ) {
    return;
  }

  let sum = 0;
  let transferCost = 0;
  let margin = 0;
  state.poItemsCopy.forEach((poItem: any) => {
    if (poItem.uniqueId === uniqueId) {
      poItem.damagedQuantityCases =
        quantity.damagedQuantityCases ?? poItem.damagedQuantityCases;
      poItem.damagedQuantityEaches =
        quantity.damagedQuantityEaches ?? poItem.damagedQuantityEaches;

      poItem.damagedQuantity =
        +poItem.damagedQuantityCases * +poItem.pack +
        +poItem.damagedQuantityEaches +
        "";

      poItem.adjustedExtendedRetail = calCulateAdjustedExtendedRetail(
        poItem.damagedQuantity,
        poItem?.retail
      );
    }
    sum += calCulateAdjustedExtendedRetail(
      poItem.damagedQuantity,
      poItem?.retail
    );
    transferCost +=
      -Number(poItem?.damagedQuantity || "0") *
      stringToNumber(poItem?.transferCost || "0");
  });
  margin = sum ? ((sum - transferCost) / sum) * 100 : 0;

  state.totalCost = roundNumber(sum);
  state.margin = +transferCost !== 0 ? roundNumber(margin) : 0;
  state.totTransferCost = roundNumber(transferCost);
};

export const handleAddAttachment = (
  state: any,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  state.attachments.push(action.payload);
};

export const handleRemoveAttachment = (
  state: any,
  action: { payload: number }
) => {
  const filterAttachments: {
    fileName: string;
    fileURL: string;
    fileType: string;
  }[] = state.attachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  state.deletedAtttachmentPaths = [
    ...state.deletedAtttachmentPaths,
    state.attachments[action?.payload].fileURL
  ];
  state.attachments = filterAttachments;
};

export const handleAddApproverAttachment = (
  state: any,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  state.approverAttachments.push(action.payload);
};

export const handleRemoveApproverAttachment = (
  state: any,
  action: { payload: number }
) => {
  const filterAttachments = state.approverAttachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  state.deletedAtttachmentPaths = [
    ...state.deletedAtttachmentPaths,
    state.approverAttachments[action?.payload].fileURL
  ];
  state.approverAttachments = filterAttachments;
};

export const handleReset = () => {
  return initialState;
};

export const handleUpdateNavigationBlocking = (state: any, action: any) => {
  state.blockNavigation = action.payload;
};

export const handleUpdateNotes = (state: any, action: any) => {
  const notes = action.payload;
  state.notes = notes;
};

export const handleUpdateApproverNotes = (state: any, action: any) => {
  const notes = action.payload;
  state.approverNotes = notes;
};

export const handleUpdateReasonForClaim = (state: any, action: any) => {
  const reasonForClaim = action.payload;
  state.reasonForClaim = reasonForClaim;
};

export const handlePendingForFetchTransferDetails = (state: any) => {
  state.getPOItemsStatus = ApiStatus.PENDING;
};

export const handleFulfilledForFetchTransferDetails = (
  state: any,
  action: any
) => {
  state.transferItems = action?.payload?.data?.poItems;
  state.getPOItemsStatus = ApiStatus.IDLE;
};

export const handleRejectedForFetchTransferDetails = (state: any) => {
  state.getPOItemsStatus = ApiStatus.IDLE;
};

export const handlePendingUploadFreightClaimAttachments = (state: any) => {
  state.uploadAttachmentLoading = true;
};

export const handleFulFillUploadFreightClaimAttachments = (state: any) => {
  state.uploadAttachmentLoading = false;
};

export const handleRejectUploadFreightClaimAttachments = (state: any) => {
  state.uploadAttachmentLoading = false;
};

export const handlePendingPostEditFreightClaim = (state: any) => {
  state.editPOLoading = true;
};

export const handleFulFillPostEditFreightClaim = (state: any) => {
  state.editPOLoading = false;
};

export const handleRejectPostEditFreightClaim = (state: any) => {
  state.editPOLoading = false;
};

export const handlePendingGetProductsForFC = (state: any, _payload: any) => {
  state.getPOItemsStatus = ApiStatus.PENDING;
  state.editPOLoading = true;
};

export const formatPoItems = (poItems: any) =>
  poItems?.map((poItem: any) => ({
    ...poItem,

    netPrice: formatCurrencyOrNumber(poItem.netPrice),
    retail: formatCurrencyOrNumber(poItem.retail),
    adjustedExtendedRetail: formatCurrencyOrNumber(
      poItem.adjustedExtendedRetail
    ),
    pack: poItem.packOrSize.split("/")[0],
    damagedQuantity:
      +poItem.damagedQuantityCases * +poItem.packOrSize.split("/")[0] +
      +poItem.damagedQuantityEaches +
      "",
    initalDamagedQuantityCases: poItem.damagedQuantityCases || 0,
    initalDamagedQuantityEaches: poItem.damagedQuantityEaches || 0,
    uniqueId: uuidv4(),
    inputDisabled: {
      damagedQuantityCases: poItem.isRandomWeight || poItem.palletModId
    },
    totalEaches: calculateEaches(poItem, "FC")
  }));

const getPoAdditionalDetail = (data: any) => ({
  docId: data?.docId,
  isStoreCredit: true,
  invoiceLabel: data?.invoiceLabel,
  wareHouse: data?.wareHouse,
  supplier: data?.supplier,
  internalId: data.internalId
});

export const handleFulFillGetProductsForFC = (
  state: any,
  { payload }: { payload: any }
) => {
  state.editPOLoading = false;
  if (payload?.data) {
    const poItemsMapped = mapPoItemsWithStoreCreditDetails(
      payload?.data?.poItems,
      state,
      "FC"
    );
    const poItems = formatPoItems(poItemsMapped);
    state.poItems = poItems;
    state.poItemsCopy = poItems;
    state.poAdditionalDetails = getPoAdditionalDetail(payload.data);
    state.poBasicData.createdBy = payload.data?.createdByUser;
    state.poBasicData.inPalletReceivingProgram =
      payload.data.inPalletReceivingProgram;
    state.poBasicData.purchasingOrganization =
      payload.data?.purchasingOrganization;
    state.attachments = payload.data?.attachments;
    state.notes = payload.data?.notes;
    state.notesCopy = payload.data?.notes;
    state.reasonForClaim = payload.data?.reasonForClaim;
    state.totalCost = roundNumber(payload.data?.totalExtendedRetail);
    state.margin = roundNumber(payload.data?.margin);
    state.getPOItemsStatus = ApiStatus.IDLE;
    state.totTransferCost = roundNumber(payload.data?.totalTransfer);

    const data = payload.data;

    state.internalId = data.internalId;
    state.poBasicData.poType = data.storeCreditType;
    state.poBasicData.effectiveDate = data.effectiveDate;
    state.wareHouse = data?.wareHouse;
    state.poBasicData.purchasingOrganization = data.purchasingOrganization;
    state.notes = data.notes;
    state.status = data.status;
    state.referenceTransferReceivedDate = data.referenceTransferReceivedDate;
    state.approvedOrRejectedDate = data.approvedOrRejectedDate;
    state.poBasicData.invoiceNumber = data?.docId;
    state.poBasicData.invoiceLabel = data?.invoiceLabel;
    state.poBasicData.supplier = data?.supplier;
    state.invoiceLabel = data?.invoiceLabel;
    state.storeId = data?.storeId;
    state.refTransfer = data?.referenceTransfer;
    state.approverNotes = data?.approverNotes;
    state.approverNotesCopy = data?.approverNotes;
    state.approverAttachments = data?.approverAttachments;
  }
};

export const handleRejectGetProductsForFC = (state: any) => {
  state.getPOItemsStatus = ApiStatus.ERROR;
  state.editPOLoading = false;
};

export const handleCombineFcAndTransferItems = (state: any) => {
  const fcItems = state.poItemsCopy;

  const transferItemsNotInFc = state.transferItems.filter(
    (item: any) =>
      fcItems.findIndex((fcItem: any) => matchPalletItem(item, fcItem)) === -1
  );

  const formattedTransferItems = transferItemsNotInFc.map((item: any) =>
    freightClaimPoItemMapper(item)
  );

  state.poItemsCopy = [...fcItems, ...formattedTransferItems];
  state.poItems = [...fcItems, ...formattedTransferItems];
};

export const handleSelectAllPalletItems = (
  state: any,
  action: {
    payload: { palletId: string; selectedState: boolean; headerId: string };
  }
) => {
  const { palletId, selectedState } = action.payload;

  let sum = 0;
  let transferCost = 0;
  let margin = 0;
  const updatedFcItems = state.poItemsCopy.map((poItem: any) => {
    if (poItem.palletId !== palletId) {
      sum += -(
        Number(poItem?.damagedQuantity || 0) *
        Number(poItem?.retail?.slice(1)?.replace(/,/g, "") || 0)
      );
      transferCost +=
        -Number(poItem?.damagedQuantity || "0") *
        stringToNumber(poItem?.transferCost || "0");
      return poItem;
    }

    mapDamagedQuantityWithReceived(poItem, selectedState);

    sum += -(
      Number(poItem?.damagedQuantity || 0) *
      Number(poItem?.retail?.slice(1)?.replace(/,/g, "") || 0)
    );
    transferCost +=
      -Number(poItem?.damagedQuantity || "0") *
      stringToNumber(poItem?.transferCost || "0");

    return poItem;
  });

  state.poItemsCopy = updatedFcItems;
  margin = sum ? ((sum - transferCost) / sum) * 100 : 0;
  state.totalCost = roundNumber(sum);
  state.margin = +transferCost === 0 ? 0 : roundNumber(margin);
  state.totTransferCost = roundNumber(transferCost);
};

export const handleGetStoreCreditDetailsForFcPending = (state: {
  getStoreCreditDetailsStatus: IApiStatus;
}) => {
  state.getStoreCreditDetailsStatus = ApiStatus.PENDING;
};

export const handleGetStoreCreditDetailsForFcRejected = (state: {
  getStoreCreditDetailsStatus: IApiStatus;
}) => {
  state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
};

export const handleGetStoreCreditDetailsForFcFulfilled = (
  state: {
    overShortDocId: string;
    getStoreCreditDetails: IGetStoreCreditDetails[];
    getStoreCreditDetailsStatus: IApiStatus;
  },
  {
    payload
  }: { payload: { data: { docId: string; poItems: IGetStoreCreditDetails[] } } }
) => {
  if (payload?.data) {
    state.overShortDocId = payload.data?.docId;
    state.getStoreCreditDetails = payload.data?.poItems;
    state.getStoreCreditDetailsStatus = ApiStatus.IDLE;
  } else {
    state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
  }
};
